import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "accessTag",
    "notionSearch",
    "notionFrame",
    "notionPageId",
    "workflowPanel",
    "workflowSelect",
    "regenerateButton",
    "regenerateStatus",
    "title",
    "videoUrl",
    "generationMetadata",
  ];

  static values = {
    notionCorpusUrl: String,
    regenerateUrl: String,
    regenerateMissingAudience: String,
    regenerateError: String,
    notionCorpusIdleHint: String,
    subProductWorkflows: Object,
    workflowGenericLabel: String,
  };

  connect() {
    this.refreshNotionCorpus = this.refreshNotionCorpus.bind(this);
    this.debouncedRefreshNotionCorpus = this.debounce(this.refreshNotionCorpus, 300);

    if (this.hasNotionSearchTarget) {
      this.notionSearchTarget.addEventListener("input", this.debouncedRefreshNotionCorpus);
    }

    if (this.hasAccessTagInputs()) {
      this.tagsChanged();
    } else if (this.hasWorkflowSelectTarget && this.hasSubProductWorkflowsValue) {
      this.rebuildWorkflowSelect();
    }

    if (this.hasNotionFrameTarget) {
      this.onNotionFrameLoad = this.onNotionFrameLoad.bind(this);
      this.notionFrameTarget.addEventListener("turbo:frame-load", this.onNotionFrameLoad);
    }
  }

  disconnect() {
    window.clearTimeout(this.notionRefreshTimeout);
    if (this.hasNotionFrameTarget) {
      this.notionFrameTarget.removeEventListener("turbo:frame-load", this.onNotionFrameLoad);
    }
  }

  onNotionFrameLoad() {
    this.bindNotionPageSelection();
  }

  bindNotionPageSelection() {
    if (!this.hasNotionFrameTarget) return;

    this.notionFrameTarget.querySelectorAll('input[name="notion_page_id"]').forEach((input) => {
      if (input.dataset.academyArticleFormBound === "true") return;
      input.dataset.academyArticleFormBound = "true";
      input.addEventListener("change", () => this.prefillTitleFromNotionPage(input));
    });
  }

  prefillTitleFromNotionPage(input) {
    if (!input.checked || !input.value || !this.hasTitleTarget) return;

    const label = this.notionFrameTarget.querySelector(`label[for="${input.id}"]`);
    const titleSpan = label?.querySelector(".fw-semibold");
    const title = titleSpan?.textContent?.trim();
    if (title) {
      this.titleTarget.value = title;
    }
  }

  tagsChanged() {
    this.toggleWorkflowPanel();
    this.rebuildWorkflowSelect();
    this.refreshNotionCorpus();
  }

  workflowChanged() {
    this.refreshNotionCorpus();
  }

  toggleWorkflowPanel() {
    if (!this.hasWorkflowPanelTarget) return;

    const hasSubproduct = this.selectedAccessTags().some((tag) => tag.startsWith("subproduct:"));
    this.workflowPanelTarget.classList.toggle("d-none", !hasSubproduct);
  }

  rebuildWorkflowSelect() {
    if (!this.hasWorkflowSelectTarget || !this.hasSubProductWorkflowsValue) return;

    const subProductSlugs = this.selectedAccessTags()
      .filter((tag) => tag.startsWith("subproduct:"))
      .map((tag) => tag.split(":", 2)[1]);

    if (subProductSlugs.length === 0) return;

    const merged = new Map();
    subProductSlugs.forEach((slug) => {
      (this.subProductWorkflowsValue[slug] || []).forEach((entry) => merged.set(entry.key, entry));
    });

    const options = Array.from(merged.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" })
    );

    const currentValue = this.workflowSelectTarget.value;
    this.workflowSelectTarget.innerHTML = "";

    const blankOption = document.createElement("option");
    blankOption.value = "";
    blankOption.textContent = this.workflowGenericLabelValue;
    this.workflowSelectTarget.appendChild(blankOption);

    options.forEach((workflow) => {
      const option = document.createElement("option");
      option.value = workflow.key;
      option.textContent = workflow.label;
      this.workflowSelectTarget.appendChild(option);
    });

    if (currentValue && options.some((workflow) => workflow.key === currentValue)) {
      this.workflowSelectTarget.value = currentValue;
    }
  }

  async regenerate(event) {
    event.preventDefault();

    const accessTags = this.selectedAccessTags();
    if (!accessTags.some((tag) => tag.startsWith("audience:"))) {
      this.showStatus(this.regenerateMissingAudienceValue, true);
      return;
    }

    this.setRegenerating(true);
    this.showStatus("", false);

    try {
      const formData = new FormData();
      accessTags.forEach((tag) => formData.append("access_tags[]", tag));

      const notionPageId = this.selectedNotionPageId();
      if (notionPageId) formData.append("notion_page_id", notionPageId);

      const workflowType = this.hasWorkflowSelectTarget ? this.workflowSelectTarget.value : "";
      if (workflowType) formData.append("workflow_type", workflowType);

      const response = await fetch(this.regenerateUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok) {
        const message = Array.isArray(payload.errors) ? payload.errors.join(", ") : this.regenerateErrorValue;
        this.showStatus(message, true);
        return;
      }

      this.applyGeneratedPayload(payload);
      const warnings = Array.isArray(payload.warnings) ? payload.warnings.filter(Boolean) : [];
      if (warnings.length > 0) {
        this.showStatus(warnings.join(" "), false);
      } else {
        this.showStatus("", false);
      }
    } catch (_error) {
      this.showStatus(this.regenerateErrorValue, true);
    } finally {
      this.setRegenerating(false);
    }
  }

  applyGeneratedPayload(payload) {
    if (this.hasTitleTarget && payload.title) {
      this.titleTarget.value = payload.title;
    }
    if (this.hasGenerationMetadataTarget && payload.generation_metadata) {
      this.generationMetadataTarget.value = JSON.stringify(payload.generation_metadata);
    }

    this.applyContentToEditor(payload);
  }

  applyContentToEditor(payload) {
    const editorElement = this.element.querySelector('[data-controller~="academy-article-editor"]');
    if (!editorElement) return;

    const editorController = this.application.getControllerForElementAndIdentifier(
      editorElement,
      "academy-article-editor"
    );

    if (editorController) {
      editorController.applyGeneratedContent(payload);
      return;
    }

    this.dispatch("contentGenerated", {
      detail: payload,
      bubbles: true,
    });
  }

  refreshNotionCorpus() {
    if (!this.hasNotionFrameTarget) return;

    const accessTags = this.selectedAccessTags();
    const query = this.hasNotionSearchTarget ? this.notionSearchTarget.value.trim() : "";
    const hasAudience = accessTags.some((tag) => tag.startsWith("audience:"));

    if (!hasAudience && !query) {
      this.notionFrameTarget.removeAttribute("src");
      const hint = this.notionCorpusIdleHintValue || this.regenerateMissingAudienceValue || "";
      this.notionFrameTarget.innerHTML = hint
        ? `<p class="small text-muted mb-0">${hint}</p>`
        : "";
      return;
    }

    const url = new URL(this.notionCorpusUrlValue, window.location.origin);
    accessTags.forEach((tag) => url.searchParams.append("access_tags[]", tag));

    const workflowType = this.hasWorkflowSelectTarget ? this.workflowSelectTarget.value : "";
    if (workflowType) url.searchParams.set("workflow_type", workflowType);

    if (query) {
      url.searchParams.set("q", query);
    } else {
      url.searchParams.delete("q");
    }

    const selected = this.selectedNotionPageId();
    if (selected) {
      url.searchParams.set("selected", selected);
    }

    this.notionFrameTarget.src = `${url.pathname}${url.search}`;
  }

  selectedAccessTags() {
    if (!this.hasAccessTagTarget) return [];

    return this.accessTagTargets
      .filter((input) => input.tagName === "INPUT")
      .filter((input) => input.type === "hidden" || input.checked)
      .map((input) => input.value)
      .filter(Boolean);
  }

  hasAccessTagInputs() {
    return this.accessTagTargets.some((el) => el.tagName === "INPUT");
  }

  selectedNotionPageId() {
    if (this.hasNotionPageIdTarget && this.notionPageIdTarget.value) {
      return this.notionPageIdTarget.value;
    }

    if (!this.hasNotionFrameTarget) return "";

    const selected = this.notionFrameTarget.querySelector('input[name="notion_page_id"]:checked');
    return selected?.value || "";
  }

  setRegenerating(active) {
    if (!this.hasRegenerateButtonTarget) return;

    this.regenerateButtonTarget.disabled = active;
    this.regenerateButtonTarget.classList.toggle("disabled", active);
  }

  showStatus(message, isError) {
    if (!this.hasRegenerateStatusTarget) return;

    this.regenerateStatusTarget.textContent = message;
    this.regenerateStatusTarget.classList.toggle("d-none", !message);
    this.regenerateStatusTarget.classList.toggle("text-danger", isError);
    this.regenerateStatusTarget.classList.toggle("text-muted", !isError);
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  debounce(fn, delay) {
    return (...args) => {
      window.clearTimeout(this.notionRefreshTimeout);
      this.notionRefreshTimeout = window.setTimeout(() => fn(...args), delay);
    };
  }
}
